/**
 * Guide page behaviour (sections/developer-guide.liquid). Progressive enhancement only:
 * without JS the guide reads fine, code blocks just have no Copy button and the
 * "On this page" list has no current item.
 *
 * <bk-code-copies> — wraps the guide's .bk-prose. The sync renders code blocks as
 *   div.bk-code > div.bk-code-bar > [span.bk-code-lang] + pre > code, without a button.
 *   For each block this gives the <code> an id and appends a clone of the element's
 *   <template> (the rendered snippets/copy-button.liquid, i.e. <bk-copy>) to the bar, as in
 *   the design. A bar without a language gets an empty .bk-code-lang so the button keeps
 *   to the right (the bar is justify-content: space-between). The <pre> gets tabindex="0" so a
 *   block that scrolls sideways can be scrolled from the keyboard.
 *
 * <bk-toc-spy> — wraps the "On this page" list. Marks the link of the section being read
 *   with aria-current="location" (the design marks the current item), updating on scroll.
 */

class BkCodeCopies extends HTMLElement {
  connectedCallback() {
    const template = this.querySelector(':scope > template');
    if (!template || this.dataset.enhanced) return;
    this.dataset.enhanced = 'true';
    const prefix = this.dataset.idPrefix || 'bk-code';
    this.querySelectorAll('.bk-prose .bk-code').forEach((block, index) => {
      const bar = block.querySelector(':scope > .bk-code-bar');
      const code = block.querySelector(':scope > pre > code');
      if (!bar || !code) return;
      if (!code.id) code.id = `${prefix}-${index + 1}`;
      // Long lines scroll sideways: keyboard users must be able to focus the block to scroll it.
      if (!code.parentElement.hasAttribute('tabindex')) code.parentElement.tabIndex = 0;
      if (!bar.querySelector('.bk-code-lang')) {
        const spacer = document.createElement('span');
        spacer.className = 'bk-code-lang';
        bar.prepend(spacer);
      }
      const copy = template.content.cloneNode(true);
      const element = copy.querySelector('bk-copy');
      if (element) element.setAttribute('data-copy-target', code.id);
      bar.append(copy);
    });
  }
}

class BkTocSpy extends HTMLElement {
  connectedCallback() {
    this.links = Array.from(this.querySelectorAll('a[href^="#"]'));
    this.targets = this.links
      .map((link) => document.getElementById(decodeURIComponent(link.getAttribute('href').slice(1))))
      .filter(Boolean);
    if (!this.targets.length) return;
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  update() {
    // The current section is the last heading that has reached the top quarter of the viewport.
    const line = Math.min(window.innerHeight * 0.25, 160);
    let current = this.targets[0];
    for (const target of this.targets) {
      if (target.getBoundingClientRect().top - line <= 0) current = target;
      else break;
    }
    // At the very bottom the last section is current even if its heading cannot reach the line.
    const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
    if (atBottom) current = this.targets[this.targets.length - 1];
    for (const link of this.links) {
      const id = decodeURIComponent(link.getAttribute('href').slice(1));
      if (current && id === current.id) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    }
  }
}

if (!customElements.get('bk-code-copies')) customElements.define('bk-code-copies', BkCodeCopies);
if (!customElements.get('bk-toc-spy')) customElements.define('bk-toc-spy', BkTocSpy);
